import { Box, Scissors, Trash2 } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { resolveCollectionCutterRef } from "../../app/cutterCollections.ts";
import { getSingleSelectionTarget } from "../../app/objectSelection.ts";
import { useStore } from "../../app/store.ts";
import { Button } from "../Button.tsx";
import { SidebarCard } from "../SidebarCard.tsx";
import { ListControl, SectionLabel } from "../shared.tsx";
import {
  COLOR_BORDER_STRONG,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  FONT_SIZE_SMALL,
  FONT_SIZE_TINY,
} from "../tokens.ts";

export function CutterPanel({ collectionId }: { collectionId: string }) {
  const bakeCutterCollection = useStore((s) => s.bakeCutterCollection);
  const deleteCutterCollectionCut = useStore(
    (s) => s.deleteCutterCollectionCut,
  );
  const deleteCutterCollectionCuts = useStore(
    (s) => s.deleteCutterCollectionCuts,
  );
  const selectObjectTarget = useStore((s) => s.selectObjectTarget);
  const renameSceneObject = useStore((s) => s.renameSceneObject);
  const [pending, setPending] = useState(false);

  const collection = useStore((s) => s.collections.get(collectionId));
  const sceneObjects = useStore((s) => s.sceneObjects);
  const modifierOverrides = useStore((s) => s.modifierOverrides);
  const cutterMembers = useMemo(() => {
    if (!collection) return null;
    const ref = resolveCollectionCutterRef(
      collection,
      sceneObjects,
      modifierOverrides,
    );
    if (!ref) return null;
    const host = sceneObjects.get(ref.hostObjectId);
    if (!host) return null;
    return {
      host: { id: host.id, name: host.name },
      cutters: [...ref.cutterObjectIds]
        .map((id) => {
          const object = sceneObjects.get(id);
          return object ? { id: object.id, name: object.name } : null;
        })
        .filter((object): object is { id: string; name: string } => !!object),
      memberCount: collection.objectIds.size,
    };
  }, [collection, modifierOverrides, sceneObjects]);
  const selectedObjectId = useStore((s) => {
    const target = getSingleSelectionTarget(s.selectedObjectTargets);
    return target?.kind === "sceneObject" ? target.id : null;
  });

  const items = useMemo(
    () =>
      cutterMembers
        ? [
            {
              id: cutterMembers.host.id,
              label: cutterMembers.host.name,
              trailing: (
                <RoleBadge title="Primary cutter object">Primary</RoleBadge>
              ),
              leading: (
                <span title="Primary object">
                  <Box size={12} />
                </span>
              ),
            },
            ...cutterMembers.cutters.map((cutter, index) => ({
              id: cutter.id,
              label: cutter.name,
              trailing: (
                <RoleBadge>{`Cut ${index + 1}`}</RoleBadge>
              ),
            })),
          ]
        : [],
    [cutterMembers],
  );
  const hostId = cutterMembers?.host.id ?? null;
  const activeId =
    selectedObjectId && items.some((item) => item.id === selectedObjectId)
      ? selectedObjectId
      : hostId;
  const cutterCount = cutterMembers?.cutters.length ?? 0;
  const memberCount = cutterMembers?.memberCount ?? 0;

  const handleBake = useCallback(async () => {
    if (pending) return;
    setPending(true);
    try {
      await bakeCutterCollection(collectionId);
    } finally {
      setPending(false);
    }
  }, [bakeCutterCollection, collectionId, pending]);

  const handleDeleteCuts = useCallback(() => {
    if (pending) return;
    deleteCutterCollectionCuts(collectionId);
  }, [collectionId, deleteCutterCollectionCuts, pending]);

  const handleSelect = useCallback(
    (id: string) => {
      selectObjectTarget({ kind: "sceneObject", id }, false);
    },
    [selectObjectTarget],
  );
  const handleDeleteActive = useCallback(
    (id: string) => {
      if (pending || id === hostId) return;
      deleteCutterCollectionCut(collectionId, id);
    },
    [collectionId, deleteCutterCollectionCut, hostId, pending],
  );
  const canDeleteActive = useCallback(
    (_items: typeof items, activeItem: (typeof items)[number] | null) =>
      !pending && !!activeItem && activeItem.id !== hostId,
    [hostId, items, pending],
  );

  if (!cutterMembers || cutterCount === 0) return null;

  return (
    <SidebarCard style={{ overflow: "hidden" }}>
      <SectionLabel>Cutter</SectionLabel>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 3,
          fontSize: FONT_SIZE_SMALL,
          color: COLOR_TEXT_SECONDARY,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 8,
            fontSize: FONT_SIZE_TINY,
            color: COLOR_TEXT_FAINT,
          }}
        >
          <span>Members</span>
          <span>
            {cutterCount + 1} / {memberCount}
          </span>
        </div>
        <ListControl
          items={items}
          activeId={activeId}
          onSelect={handleSelect}
          onDelete={handleDeleteActive}
          onRename={renameSceneObject}
          canDelete={canDeleteActive}
          emptyMessage="No cutters"
          maxHeight={156}
        />
      </div>

      <div
        style={{
          marginTop: 8,
          paddingTop: 6,
          borderTop: `0.5px solid ${COLOR_BORDER_STRONG}`,
          display: "flex",
          justifyContent: "space-between",
          gap: 6,
          flexWrap: "wrap",
        }}
      >
        <Button
          type="button"
          onClick={handleDeleteCuts}
          disabled={pending}
          variant="danger"
          size="xs"
          leftIcon={<Trash2 size={12} />}
        >
          Delete All Cuts
        </Button>
        <Button
          type="button"
          onClick={handleBake}
          loading={pending}
          variant="secondary"
          size="xs"
          leftIcon={<Scissors size={12} />}
        >
          Bake Cuts
        </Button>
      </div>
    </SidebarCard>
  );
}

function RoleBadge({
  children,
  title,
}: {
  children: string;
  title?: string;
}) {
  return (
    <span
      title={title}
      style={{
        fontSize: FONT_SIZE_TINY,
        color: COLOR_TEXT_FAINT,
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}
